import { SetStateAction, useCallback, useMemo, useState, Dispatch } from 'react'
import { DrawerProps } from 'components/Drawer/types'

const INITIAL_POPUP_PROPS: Partial<DrawerProps> = {
  visible: false,
  title: ''
}

const useDrawer = <TPayload extends object = {}>(
  props: Partial<DrawerProps> = INITIAL_POPUP_PROPS
) => {
  const [_state, _setState] = useState<DrawerProps & { payload: TPayload }>({
    payload: {} as TPayload,
    ...props
  })

  const close = useCallback(() => {
    _setState((prev) => ({ ...prev, visible: false }))
  }, [])

  const open = useCallback((payload: TPayload = {} as TPayload) => {
    _setState((prev) => ({ ...prev, visible: true, payload }))
  }, [])

  const state = useMemo(
    () => ({
      visible: _state.visible,
      title: _state.title,
      width: _state.width,
      payload: _state.payload,
      onClose: close
    }),
    [_state.visible, _state.title, _state.width, _state.payload, close]
  )
  const actions = useMemo(
    () => ({ close, open, setState: _setState }),
    [close, open]
  )

  return { state, actions }
}

export type DrawerReturnType = ReturnType<typeof useDrawer>
export type DrawerReturnState = ReturnType<typeof useDrawer>['state']
export type DrawerReturnActions = ReturnType<typeof useDrawer>['actions']
export type DrawerReturnTypeWithPayload<TPayload extends object = {}> = {
  state: Omit<DrawerReturnState, 'payload'> & { payload: TPayload }
  actions: Omit<DrawerReturnActions, 'open' | 'setState'> & {
    open: (payload?: TPayload) => void
    setState: Dispatch<SetStateAction<DrawerProps & { payload: TPayload }>>
  }
}

export default useDrawer
